import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { faqData } from './faqData.js';
import { useCanonical } from '../../hooks/useCanonical.js';

const CATEGORY_ICONS: Record<string, string> = {
  はじめての方へ: '🚲',
  ユーザー: '👤',
  スポット: '📍',
  ユーザーランキング: '🏆',
  技術的な質問: '⚙️',
  'マナー・安全について': '🛡️',
};

function AnswerText({ text }: { text: string }) {
  return <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{text}</p>;
}

export function FaqView() {
  useCanonical('https://ridehub.shiget.net/faq');
  const navigate = useNavigate();

  useEffect(() => {
    const originalTitle = document.title;
    document.title = 'よくある質問 | RideHub';

    const metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const originalDesc = metaDesc?.content ?? '';
    if (metaDesc) {
      metaDesc.content =
        'RideHubのよくある質問。チェックイン・スポット登録・ユーザーランク・プッシュ通知など、RideHubの使い方に関する疑問にお答えします。';
    }

    // プリレンダリング済みHTMLに既にJSON-LDが含まれている場合は重複を避けてスキップ
    const existing = document.head.querySelector('script[type="application/ld+json"]');
    if (existing) {
      return () => {
        document.title = originalTitle;
        if (metaDesc) metaDesc.content = originalDesc;
      };
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqData.flatMap((category) =>
        category.items.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        }))
      ),
    });
    document.head.appendChild(script);

    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.content = originalDesc;
      document.head.removeChild(script);
    };
  }, []);
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleItem = (key: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className="max-w-lg mx-auto pb-10">
      {/* ヘッダー */}
      <div className="flex items-center gap-3 p-4 mb-2">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-700"
          aria-label="戻る"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className="text-xl font-bold">よくある質問</h1>
      </div>

      {/* カテゴリ別セクション */}
      <div className="px-4 space-y-6">
        {faqData.map((category, catIndex) => (
          <section key={catIndex}>
            {/* カテゴリ見出し */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">{CATEGORY_ICONS[category.title] ?? '❓'}</span>
              <h2 className="text-sm font-bold text-gray-800">{category.title}</h2>
              <span className="text-xs text-gray-400 ml-auto">{category.items.length}件</span>
            </div>

            {/* 質問リスト */}
            <div className="rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
              {category.items.map((item, itemIndex) => {
                const key = `${catIndex}-${itemIndex}`;
                const isOpen = openItems.has(key);
                return (
                  <div key={itemIndex} className="bg-white">
                    {/* 質問行 */}
                    <button
                      onClick={() => toggleItem(key)}
                      className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                    >
                      <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                        Q
                      </span>
                      <span className="flex-1 text-sm text-gray-800 leading-snug font-medium">
                        {item.question}
                      </span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        className={`flex-shrink-0 mt-0.5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                      >
                        <path
                          d="M6 9L12 15L18 9"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    {/* 回答 */}
                    {isOpen && (
                      <div className="flex items-start gap-3 px-4 py-3 bg-gray-50 border-t border-gray-100">
                        <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">
                          A
                        </span>
                        <AnswerText text={item.answer} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* フッター */}
      <p className="mt-8 px-4 text-center text-xs text-gray-400">
        解決しない場合はフィードバックからお知らせください
      </p>
    </div>
  );
}
